/**
 * Superadmin management (ADR-0036 Fase 2 — F2.1). List / create / enable-disable other
 * superadmins, reset their password or 2FA. Guards mirror the backend: you can't disable
 * yourself, and the backend refuses disabling the last active admin.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2, Pause, Play, Plus, ShieldOff, TriangleAlert } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type PlatformAdminRow,
  PlatformApiError,
  platformApi,
  usePlatformAuth,
} from "@/lib/platform";

const ADMINS_KEY = ["platform", "admins"];

export function PlatformAdminsPage() {
  const qc = useQueryClient();
  const me = usePlatformAuth((s) => s.admin);
  const [creating, setCreating] = React.useState(false);
  const [resetPwTarget, setResetPwTarget] = React.useState<PlatformAdminRow | null>(null);

  const listQ = useQuery({ queryKey: ADMINS_KEY, queryFn: () => platformApi.listAdmins() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ADMINS_KEY });

  const setState = useMutation({
    mutationFn: ({ id, state }: { id: string; state: "active" | "disabled" }) =>
      platformApi.updateAdmin(id, { state }),
    onSuccess: invalidate,
  });
  const reset2fa = useMutation({
    mutationFn: (id: string) => platformApi.resetAdmin2fa(id),
    onSuccess: invalidate,
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Amministratori"
        description="I superadmin del control plane. Non puoi disabilitare te stesso o l'ultimo attivo."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Nuovo admin
          </Button>
        }
      />
      <PageContent className="space-y-4">
        {setState.isError && (
          <p className="text-sm text-danger">
            {setState.error instanceof PlatformApiError && setState.error.message.includes("last")
              ? "Non puoi disabilitare l'ultimo amministratore attivo."
              : setState.error instanceof PlatformApiError
                ? setState.error.message
                : "Operazione non riuscita."}
          </p>
        )}

        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare gli amministratori"
            description={
              listQ.error instanceof PlatformApiError ? listQ.error.message : "Errore di rete."
            }
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Email</Th>
                  <Th>Nome</Th>
                  <Th>Stato</Th>
                  <Th>2FA</Th>
                  <Th>Ultimo accesso</Th>
                  <Th className="w-32 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const isSelf = me?.id === row.id;
                  return (
                    <Tr key={row.id}>
                      <Td className="font-medium">
                        {row.email}
                        {isSelf && <span className="ml-2 text-xs text-muted-foreground">(tu)</span>}
                      </Td>
                      <Td className="text-sm text-muted-foreground">{row.full_name || "—"}</Td>
                      <Td>
                        <Badge tone={row.state === "active" ? "active" : "neutral"}>
                          {row.state === "active" ? "Attivo" : "Disabilitato"}
                        </Badge>
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {row.totp_enabled ? "Attiva" : "—"}
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {row.last_login_at ? new Date(row.last_login_at).toLocaleString() : "mai"}
                      </Td>
                      <Td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {row.state === "active" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isSelf || setState.isPending}
                              title={isSelf ? "Non puoi disabilitarti" : "Disabilita"}
                              onClick={() => setState.mutate({ id: row.id, state: "disabled" })}
                            >
                              <Pause className="h-4 w-4 text-warning" strokeWidth={1.5} />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={setState.isPending}
                              title="Riattiva"
                              onClick={() => setState.mutate({ id: row.id, state: "active" })}
                            >
                              <Play className="h-4 w-4 text-success" strokeWidth={1.5} />
                            </Button>
                          )}
                          {row.totp_enabled && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={reset2fa.isPending}
                              title="Reimposta 2FA"
                              onClick={() => reset2fa.mutate(row.id)}
                            >
                              <ShieldOff className="h-4 w-4 text-danger" strokeWidth={1.5} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Reimposta password"
                            onClick={() => setResetPwTarget(row)}
                          >
                            <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      {creating && <CreateAdminDialog onClose={() => setCreating(false)} onCreated={invalidate} />}
      {resetPwTarget && (
        <ResetPasswordDialog admin={resetPwTarget} onClose={() => setResetPwTarget(null)} />
      )}
    </PageContainer>
  );
}

function CreateAdminDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [password, setPassword] = React.useState("");

  const create = useMutation({
    mutationFn: () =>
      platformApi.createAdmin({
        email: email.trim().toLowerCase(),
        full_name: fullName.trim() || undefined,
        password,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const error = create.isError
    ? create.error instanceof PlatformApiError && create.error.status === 409
      ? "Esiste già un amministratore con questa email."
      : create.error instanceof PlatformApiError
        ? create.error.message
        : "Creazione non riuscita."
    : null;
  const valid = email.includes("@") && password.length >= 8;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo amministratore</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="adm-email">Email</Label>
            <Input
              id="adm-email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adm-name">Nome (opzionale)</Label>
            <Input id="adm-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adm-pw">Password iniziale</Label>
            <Input
              id="adm-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min 8 caratteri"
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Annulla
          </Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ admin, onClose }: { admin: PlatformAdminRow; onClose: () => void }) {
  const [password, setPassword] = React.useState("");
  const reset = useMutation({
    mutationFn: () => platformApi.resetAdminPassword(admin.id, password),
    onSuccess: onClose,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reimposta password — {admin.email}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Le sessioni attive di questo amministratore verranno terminate.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="rpw">Nuova password</Label>
            <Input
              id="rpw"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min 8 caratteri"
            />
          </div>
          {reset.isError && (
            <p className="text-sm text-danger">
              {reset.error instanceof PlatformApiError
                ? reset.error.message
                : "Operazione non riuscita."}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
            Annulla
          </Button>
          <Button onClick={() => reset.mutate()} disabled={password.length < 8 || reset.isPending}>
            {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            Reimposta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
